/**
 * Pulzena Comparateur — product comparison tool
 * localStorage + floating bar + compare page + GA4 tracking
 */
(function () {
  'use strict';

  /* ═══════════════════════════════════════════════════
     1. localStorage manager with Safari private fallback
     ═══════════════════════════════════════════════════ */
  var STORAGE_KEY = 'pulzena_compare';
  var MAX_ITEMS = 3;
  var memoryFallback = null;

  function storageAvailable() {
    try {
      var t = '__pz_test__';
      localStorage.setItem(t, '1');
      localStorage.removeItem(t);
      return true;
    } catch (e) {
      return false;
    }
  }

  var useMemory = !storageAvailable();
  if (useMemory) memoryFallback = [];

  var PulzenaCompare = {
    getItems: function () {
      if (useMemory) return memoryFallback.slice();
      try {
        var raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    },

    _save: function (items) {
      if (useMemory) {
        memoryFallback = items;
      } else {
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch (e) {
          useMemory = true;
          memoryFallback = items;
        }
      }
      document.dispatchEvent(new CustomEvent('pulzena:compare-updated'));
    },

    toggleItem: function (product) {
      // Returns: 'added', 'removed', or 'max'
      if (this.isInCompare(product.handle)) {
        this.removeItem(product.handle);
        return 'removed';
      }
      var items = this.getItems();
      if (items.length >= MAX_ITEMS) {
        this._showToast('Vous ne pouvez comparer que ' + MAX_ITEMS + ' produits maximum');
        return 'max';
      }
      items.push({
        handle: product.handle,
        name: product.name,
        image: product.image,
        price: product.price,
        variantId: product.variantId,
        url: product.url
      });
      this._save(items);
      this._ga4('compare_add', product);
      return 'added';
    },

    addItem: function (product) {
      var result = this.toggleItem(product);
      return result === 'added';
    },

    removeItem: function (handle) {
      var items = this.getItems();
      var removed = null;
      items = items.filter(function (item) {
        if (item.handle === handle) { removed = item; return false; }
        return true;
      });
      this._save(items);
      if (removed) this._ga4('compare_remove', removed);
    },

    isInCompare: function (handle) {
      return this.getItems().some(function (item) { return item.handle === handle; });
    },

    clear: function () {
      this._save([]);
    },

    _ga4: function (event, product) {
      window.dataLayer = window.dataLayer || [];
      var data = { event: event };
      if (product) {
        data.product_handle = product.handle;
        data.product_name = product.name;
      }
      window.dataLayer.push(data);
    },

    _showToast: function (msg) {
      var existing = document.getElementById('pz-compare-toast');
      if (existing) existing.remove();
      var toast = document.createElement('div');
      toast.id = 'pz-compare-toast';
      toast.className = 'pz-compare-toast';
      toast.textContent = msg;
      document.body.appendChild(toast);
      requestAnimationFrame(function () { toast.classList.add('pz-compare-toast--visible'); });
      setTimeout(function () {
        toast.classList.remove('pz-compare-toast--visible');
        setTimeout(function () { toast.remove(); }, 300);
      }, 2500);
    }
  };

  /* Handle normalizer — Shopify handles may contain ® and URL-encoded chars */
  function normalizeHandle(h) {
    try { h = decodeURIComponent(h); } catch (e) { /* already decoded */ }
    return h.replace(/[®™©]/g, '').replace(/-{2,}/g, '-').replace(/(^-|-$)/g, '');
  }

  /* ═══════════════════════════════════════════════════
     2. Compare button SVG icon (balance/scale)
     ═══════════════════════════════════════════════════ */
  function createCompareSvg() {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('width', '18');
    svg.setAttribute('height', '18');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '2');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');

    var p1 = document.createElementNS(ns, 'path');
    p1.setAttribute('d', 'M16 3 L21 8 L16 13');
    svg.appendChild(p1);

    var p2 = document.createElementNS(ns, 'path');
    p2.setAttribute('d', 'M8 3 L3 8 L8 13');
    svg.appendChild(p2);

    var l1 = document.createElementNS(ns, 'line');
    l1.setAttribute('x1', '21'); l1.setAttribute('y1', '8');
    l1.setAttribute('x2', '3'); l1.setAttribute('y2', '8');
    svg.appendChild(l1);

    var l2 = document.createElementNS(ns, 'line');
    l2.setAttribute('x1', '12'); l2.setAttribute('y1', '3');
    l2.setAttribute('x2', '12'); l2.setAttribute('y2', '21');
    svg.appendChild(l2);

    return svg;
  }

  /* ═══════════════════════════════════════════════════
     3. Inject compare buttons on collection cards
     ═══════════════════════════════════════════════════ */
  function extractCardData(card) {
    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;
    var href = link.getAttribute('href');
    var handleMatch = href.match(/\/products\/([^/?#]+)/);
    if (!handleMatch) return null;
    var handle = normalizeHandle(handleMatch[1]);

    var titleEl = card.querySelector('.product-card__title, .product-card__name, [class*="title"]');
    var name = titleEl ? titleEl.textContent.trim() : handle;

    var imgEl = card.querySelector('img');
    var image = imgEl ? (imgEl.getAttribute('src') || imgEl.getAttribute('data-src') || '') : '';

    var priceEl = card.querySelector('.price, .product-card__price, [class*="price"] .money, [class*="price"]');
    var price = priceEl ? priceEl.textContent.trim() : '';

    return { handle: handle, name: name, image: image, price: price, url: href, variantId: null };
  }

  function injectCardButtons() {
    var cards = document.querySelectorAll('.product-card');
    cards.forEach(function (card) {
      if (card.querySelector('.pz-compare-btn')) return;

      var data = extractCardData(card);
      if (!data) return;

      var btn = document.createElement('button');
      btn.className = 'pz-compare-btn';
      btn.setAttribute('aria-label', 'Ajouter au comparateur');
      btn.setAttribute('data-tooltip', 'Comparer');
      btn.setAttribute('type', 'button');
      btn.appendChild(createCompareSvg());

      if (PulzenaCompare.isInCompare(data.handle)) {
        btn.classList.add('pz-compare-btn--active');
        btn.setAttribute('aria-label', 'Retirer du comparateur');
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        var result = PulzenaCompare.toggleItem(data);
        if (result === 'added') {
          btn.classList.add('pz-compare-btn--active');
          btn.setAttribute('aria-label', 'Retirer du comparateur');
        } else if (result === 'removed') {
          btn.classList.remove('pz-compare-btn--active');
          btn.setAttribute('aria-label', 'Ajouter au comparateur');
        }
        // 'max' = no visual change, toast already shown
      });

      var figure = card.querySelector('.product-card__figure, .product-card__image-wrapper, .product-card__media');
      if (figure) {
        figure.style.position = 'relative';
        figure.appendChild(btn);
      } else {
        card.style.position = 'relative';
        card.appendChild(btn);
      }
    });
  }

  /* ═══════════════════════════════════════════════════
     4. Inject compare button on product page
     ═══════════════════════════════════════════════════ */
  function injectProductPageButton() {
    if (!window.location.pathname.match(/\/products\//)) return;
    if (document.querySelector('.pz-compare-product-btn')) return;

    var buyZone = document.querySelector(
      '.product-form__submit, .shopify-payment-button, .product__buy-buttons, ' +
      '.product-form__buttons, .product-form__cart-submit, [data-add-to-cart]'
    );
    if (!buyZone) return;
    var container = buyZone.closest('.product-form__buttons, .product-form, .product__info-wrapper') || buyZone.parentElement;
    if (!container) return;

    var meta = document.querySelector('meta[property="og:url"]');
    var urlStr = meta ? meta.getAttribute('content') : window.location.pathname;
    var handleMatch = urlStr.match(/\/products\/([^/?#]+)/);
    if (!handleMatch) return;
    var handle = normalizeHandle(handleMatch[1]);

    var titleEl = document.querySelector('.product__title, h1.title, h1');
    var name = titleEl ? titleEl.textContent.trim() : handle;

    var imgEl = document.querySelector('.product-gallery img, .product__media img, .product__main-image img');
    var image = imgEl ? (imgEl.getAttribute('src') || '') : '';

    var priceEl = document.querySelector('.product__price .money, .price .money, .product__price');
    var price = priceEl ? priceEl.textContent.trim() : '';

    var variantInput = document.querySelector('input[name="id"], select[name="id"]');
    var variantId = variantInput ? variantInput.value : null;

    var data = { handle: handle, name: name, image: image, price: price, url: '/products/' + handle, variantId: variantId };

    var btn = document.createElement('button');
    btn.className = 'pz-compare-product-btn';
    btn.setAttribute('type', 'button');

    var isActive = PulzenaCompare.isInCompare(handle);
    btn.textContent = isActive ? 'Retirer du comparateur' : 'Ajouter au comparateur';
    btn.setAttribute('aria-label', btn.textContent);
    if (isActive) btn.classList.add('pz-compare-product-btn--active');

    btn.addEventListener('click', function () {
      var added = PulzenaCompare.addItem(data);
      if (added) {
        btn.textContent = 'Retirer du comparateur';
        btn.classList.add('pz-compare-product-btn--active');
      } else if (!PulzenaCompare.isInCompare(handle)) {
        btn.textContent = 'Ajouter au comparateur';
        btn.classList.remove('pz-compare-product-btn--active');
      }
      btn.setAttribute('aria-label', btn.textContent);
    });

    container.appendChild(btn);
  }

  /* ═══════════════════════════════════════════════════
     5. Floating compare bar
     ═══════════════════════════════════════════════════ */
  function updateFloatingBar() {
    var bar = document.getElementById('pulzena-compare-bar');
    if (!bar) return;

    var items = PulzenaCompare.getItems();
    var itemsContainer = bar.querySelector('.pz-compare-bar__items');
    var countEl = bar.querySelector('.pz-compare-bar__count');
    var cta = bar.querySelector('.pz-compare-bar__cta');
    if (!itemsContainer || !countEl || !cta) return;

    // Detect Shopify preview bar offset
    var previewBarOffset = 0;
    var previewBar = document.getElementById('preview-bar-iframe') || document.querySelector('iframe[src*="preview_bar"]') || document.querySelector('.preview-bar');
    if (previewBar || window.location.search.indexOf('preview_theme_id') !== -1) {
      previewBarOffset = 48;
    }

    if (items.length === 0) {
      bar.style.display = 'none';
      document.body.style.paddingBottom = '';
      return;
    }

    bar.style.display = '';
    bar.style.bottom = previewBarOffset + 'px';
    document.body.style.paddingBottom = (80 + previewBarOffset) + 'px';

    // Clear items container safely
    while (itemsContainer.firstChild) itemsContainer.removeChild(itemsContainer.firstChild);

    items.forEach(function (item) {
      var el = document.createElement('div');
      el.className = 'pz-compare-bar__item';

      var img = document.createElement('img');
      img.src = item.image ? item.image.replace(/(\.[^.]+)$/, '_100x$1') : '';
      img.alt = item.name;
      img.width = 40;
      img.height = 40;
      img.loading = 'lazy';
      el.appendChild(img);

      var info = document.createElement('div');
      info.className = 'pz-compare-bar__item-info';

      var nameEl = document.createElement('span');
      nameEl.className = 'pz-compare-bar__item-name';
      nameEl.textContent = item.name;
      info.appendChild(nameEl);

      if (item.price) {
        var priceEl = document.createElement('span');
        priceEl.className = 'pz-compare-bar__item-price';
        priceEl.textContent = item.price;
        info.appendChild(priceEl);
      }

      el.appendChild(info);

      var removeBtn = document.createElement('button');
      removeBtn.className = 'pz-compare-bar__remove';
      removeBtn.setAttribute('aria-label', 'Retirer ' + item.name + ' du comparateur');
      removeBtn.setAttribute('type', 'button');
      removeBtn.textContent = '\u00d7';
      removeBtn.addEventListener('click', function () {
        PulzenaCompare.removeItem(item.handle);
        syncCardButtons();
      });
      el.appendChild(removeBtn);

      itemsContainer.appendChild(el);
    });

    countEl.textContent = items.length;

    if (items.length >= 2) {
      cta.removeAttribute('data-disabled');
      cta.setAttribute('aria-disabled', 'false');
    } else {
      cta.setAttribute('data-disabled', 'true');
      cta.setAttribute('aria-disabled', 'true');
    }
  }

  function syncCardButtons() {
    document.querySelectorAll('.pz-compare-btn').forEach(function (btn) {
      var card = btn.closest('.product-card');
      if (!card) return;
      var data = extractCardData(card);
      if (!data) return;
      if (PulzenaCompare.isInCompare(data.handle)) {
        btn.classList.add('pz-compare-btn--active');
      } else {
        btn.classList.remove('pz-compare-btn--active');
      }
    });
  }

  /* ═══════════════════════════════════════════════════
     6. Compare page — table rendering
     ═══════════════════════════════════════════════════ */
  // Spec categories — read from Liquid snippet (source de vérité unique)
  // Fallback hardcodé si le config JSON n'est pas dans la page
  function getSpecCategories() {
    var configEl = document.getElementById('pulzena-compare-config');
    if (configEl) {
      try { return JSON.parse(configEl.textContent); } catch (e) { /* fallback */ }
    }
    // Fallback (ne devrait pas arriver sur la page comparateur)
    return [
      { name: 'Sport & Fitness', rows: [
        { key: 'sport_modes', label: 'Modes sport' },
        { key: 'gps', label: 'GPS int\u00e9gr\u00e9', bool: true },
        { key: 'water', label: '\u00c9tanch\u00e9it\u00e9' },
        { key: 'swimming', label: 'Natation', bool: true }
      ]},
      { name: 'Sant\u00e9 & Capteurs', rows: [
        { key: 'health', label: 'Fr\u00e9quence cardiaque', bool: true },
        { key: 'spo2', label: 'SpO2', bool: true },
        { key: 'ecg', label: 'ECG', bool: true },
        { key: 'sleep', label: 'Suivi du sommeil', bool: true }
      ]},
      { name: 'Performance', rows: [
        { key: 'screen', label: '\u00c9cran' },
        { key: 'battery', label: 'Autonomie batterie' }
      ]}
    ];
  }

  function formatBool(val) {
    if (!val || val === '\u2014' || val === '') return { text: '\u2715', cls: 'pz-compare-cross' };
    var v = String(val).toLowerCase().trim();
    if (v === 'true' || v === 'oui' || v === 'yes' || v === '1') return { text: '\u2713', cls: 'pz-compare-check' };
    if (v === 'false' || v === 'non' || v === 'no' || v === '0') return { text: '\u2715', cls: 'pz-compare-cross' };
    if (v.indexOf('oui') === 0) return { text: val, cls: 'pz-compare-check' };
    return { text: val, cls: '' };
  }

  function addToCart(btn, prod) {
    var vid = prod.first_variant_id || prod.variantId;
    if (!vid) { window.location.href = prod.url || '/products/' + prod.handle; return; }
    btn.textContent = '...';
    btn.disabled = true;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: vid, quantity: 1 })
    })
    .then(function (res) {
      if (!res.ok) throw new Error(res.statusText);
      btn.textContent = 'Ajout\u00e9 !';
      btn.classList.add('pz-compare-product-cta--success');
      PulzenaCompare._ga4('compare_to_cart', prod);
      setTimeout(function () {
        btn.textContent = 'Ajouter \u2192';
        btn.classList.remove('pz-compare-product-cta--success');
        btn.disabled = false;
      }, 2000);
    })
    .catch(function () {
      btn.textContent = 'Erreur';
      btn.disabled = false;
      setTimeout(function () { btn.textContent = 'Ajouter \u2192'; }, 2500);
    });
  }

  function renderComparePage() {
    var container = document.getElementById('pulzena-compare-page');
    if (!container) return;

    var dataEl = document.getElementById('pulzena-compare-data');
    var allProducts = {};
    if (dataEl) {
      try {
        var parsed = JSON.parse(dataEl.textContent);
        parsed.forEach(function (p) {
          allProducts[p.handle] = p;
          allProducts[normalizeHandle(p.handle)] = p;
        });
      } catch (e) { /* JSON parse error */ }
    }

    var selected = PulzenaCompare.getItems();
    selected.forEach(function (item) {
      if (!allProducts[item.handle]) {
        var norm = normalizeHandle(item.handle);
        if (allProducts[norm]) item._blobHandle = norm;
      }
    });

    // GA4
    if (selected.length >= 2) {
      var handles = selected.map(function (s) { return s.handle; });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'compare_view', product_count: selected.length, product_handles: handles.join(',') });
      for (var i = 0; i < handles.length; i++) {
        for (var j = i + 1; j < handles.length; j++) {
          window.dataLayer.push({ event: 'compare_pair', pair: [handles[i], handles[j]].sort().join('-vs-') });
        }
      }
    }

    while (container.firstChild) container.removeChild(container.firstChild);
    var colCount = selected.length + 1;

    // Empty state
    if (selected.length === 0) {
      var empty = document.createElement('div');
      empty.className = 'pz-compare-empty';
      var h2 = document.createElement('h2');
      h2.textContent = 'Votre comparateur est vide';
      empty.appendChild(h2);
      var p = document.createElement('p');
      p.textContent = 'Ajoutez des montres depuis nos collections pour les comparer c\u00f4te \u00e0 c\u00f4te.';
      empty.appendChild(p);
      var ctaLink = document.createElement('a');
      ctaLink.href = '/collections/all';
      ctaLink.className = 'pz-compare-empty__cta';
      ctaLink.textContent = 'Voir les montres \u2192';
      empty.appendChild(ctaLink);
      container.appendChild(empty);
      return;
    }

    var products = selected.map(function (item) {
      var full = allProducts[item.handle] || allProducts[item._blobHandle] || allProducts[normalizeHandle(item.handle)] || {};
      return Object.assign({}, item, full);
    });

    // Product cards row
    var productsDiv = document.createElement('div');
    productsDiv.className = 'pz-compare-products';
    productsDiv.style.gridTemplateColumns = '200px repeat(' + products.length + ', minmax(180px, 280px))';
    var spacer = document.createElement('div');
    productsDiv.appendChild(spacer);

    products.forEach(function (prod) {
      var col = document.createElement('div');
      col.className = 'pz-compare-product-col';

      var img = document.createElement('img');
      img.src = prod.featured_image || prod.image || '';
      img.alt = prod.title || prod.name;
      img.loading = 'lazy';
      col.appendChild(img);

      // Model name (extracted from title — last word after "Pulzena")
      var title = prod.title || prod.name || '';
      var modelMatch = title.match(/Pulzena[^\w]*(\w+)/i);
      if (modelMatch) {
        var model = document.createElement('div');
        model.className = 'pz-compare-product-model';
        model.textContent = modelMatch[1];
        col.appendChild(model);
      }

      var nameLink = document.createElement('a');
      nameLink.href = prod.url || '/products/' + prod.handle;
      nameLink.className = 'pz-compare-product-name';
      // Short name: remove "Montre Connect\u00e9e" prefix and "Pulzena Model" suffix
      var shortName = title.replace(/^Montre Connect\u00e9e\s*/i, '').replace(/\s*-?\s*Pulzena.*$/i, '').trim();
      nameLink.textContent = shortName || title;
      col.appendChild(nameLink);

      var priceSpan = document.createElement('span');
      priceSpan.className = 'pz-compare-product-price';
      priceSpan.textContent = prod.price_formatted || prod.price || '';
      col.appendChild(priceSpan);

      if (prod.compare_at_price && prod.compare_at_price > prod.price_raw) {
        var oldPrice = document.createElement('span');
        oldPrice.className = 'pz-compare-product-old-price';
        oldPrice.textContent = prod.compare_at_price_formatted || '';
        col.appendChild(oldPrice);
      }

      var hasVariants = prod.variants_count && prod.variants_count > 1;
      if (hasVariants) {
        var optLink = document.createElement('a');
        optLink.href = prod.url || '/products/' + prod.handle;
        optLink.className = 'pz-compare-product-cta';
        optLink.textContent = 'Choisir \u2192';
        col.appendChild(optLink);
      } else {
        var cartBtn = document.createElement('button');
        cartBtn.className = 'pz-compare-product-cta';
        cartBtn.setAttribute('type', 'button');
        cartBtn.textContent = 'Ajouter \u2192';
        (function (b, pr) {
          b.addEventListener('click', function () { addToCart(b, pr); });
        })(cartBtn, prod);
        col.appendChild(cartBtn);
      }

      productsDiv.appendChild(col);
    });
    container.appendChild(productsDiv);

    // Specs by category
    var specsDiv = document.createElement('div');
    specsDiv.className = 'pz-compare-specs';

    getSpecCategories().forEach(function (cat) {
      // Check if category has ANY row with data
      var catHasData = cat.rows.some(function (spec) {
        return products.some(function (prod) {
          var val = (prod.specs || {})[spec.key] || '';
          return val.trim() !== '';
        });
      });
      if (!catHasData) return;

      var catDiv = document.createElement('div');
      catDiv.className = 'pz-compare-cat';
      var catLabel = document.createElement('div');
      catLabel.className = 'pz-compare-cat__label';
      catLabel.textContent = cat.name;
      catDiv.appendChild(catLabel);
      specsDiv.appendChild(catDiv);

      cat.rows.forEach(function (spec) {
        var values = products.map(function (prod) {
          var specs = prod.specs || {};
          return specs[spec.key] || '';
        });

        // Skip row if ALL values are empty (nothing to show)
        var hasAnyValue = values.some(function (v) { return v && v.trim() !== ''; });
        if (!hasAnyValue) return;

        var row = document.createElement('div');
        row.className = 'pz-compare-row';
        row.style.gridTemplateColumns = '200px repeat(' + products.length + ', minmax(180px, 280px))';

        var label = document.createElement('div');
        label.className = 'pz-compare-row__label';
        label.textContent = spec.label;
        row.appendChild(label);

        var filledValues = values.filter(function (v) { return v && v !== '\u2014'; });
        var hasDiff = filledValues.length >= 2 && new Set(filledValues).size > 1;

        values.forEach(function (val) {
          var cell = document.createElement('div');
          cell.className = 'pz-compare-row__val';

          if (spec.bool) {
            var result = formatBool(val);
            var span = document.createElement('span');
            span.className = result.cls;
            span.textContent = result.text;
            cell.appendChild(span);
            if (hasDiff && result.cls === 'pz-compare-check') cell.classList.add('pz-compare-row__val--diff');
          } else {
            cell.textContent = val || '\u2014';
            if (hasDiff && val) cell.classList.add('pz-compare-row__val--diff');
          }

          row.appendChild(cell);
        });

        specsDiv.appendChild(row);
      });
    });
    container.appendChild(specsDiv);

    // Bottom CTA
    var bottomCta = document.createElement('div');
    bottomCta.className = 'pz-compare-bottom-cta';
    var ctaLabel = document.createElement('div');
    ctaLabel.className = 'pz-compare-bottom-cta__label';
    ctaLabel.textContent = "Besoin d'aide ?";
    bottomCta.appendChild(ctaLabel);
    var ctaP = document.createElement('p');
    ctaP.textContent = "Consultez notre guide pour trouver la montre connect\u00e9e id\u00e9ale selon votre usage.";
    bottomCta.appendChild(ctaP);
    var ctaA = document.createElement('a');
    ctaA.href = '/collections/all';
    ctaA.textContent = "Voir toutes les montres \u2192";
    bottomCta.appendChild(ctaA);
    container.appendChild(bottomCta);
  }

  /* ═══════════════════════════════════════════════════
     7. Floating bar: clear button + CTA handler
     ═══════════════════════════════════════════════════ */
  function setupBarActions() {
    var bar = document.getElementById('pulzena-compare-bar');
    if (!bar) return;

    var clearBtn = bar.querySelector('.pz-compare-bar__clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        PulzenaCompare.clear();
        syncCardButtons();
      });
    }

    var cta = bar.querySelector('.pz-compare-bar__cta');
    if (cta) {
      // Preserve preview_theme_id in compare link
      var previewMatch = window.location.search.match(/preview_theme_id=([^&]+)/);
      if (previewMatch) {
        var baseHref = cta.getAttribute('href').split('?')[0];
        cta.setAttribute('href', baseHref + '?preview_theme_id=' + previewMatch[1]);
      }
      cta.addEventListener('click', function (e) {
        if (cta.getAttribute('data-disabled') === 'true') {
          e.preventDefault();
          PulzenaCompare._showToast('Ajoutez au moins 2 produits');
        }
      });
    }
  }

  /* ═══════════════════════════════════════════════════
     8. Init + MutationObserver
     ═══════════════════════════════════════════════════ */
  function init() {
    injectCardButtons();
    updateFloatingBar();
    setupBarActions();
    if (window.location.pathname.indexOf('/pages/comparateur') !== -1) {
      renderComparePage();
    }
  }

  document.addEventListener('pulzena:compare-updated', updateFloatingBar);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var observerTarget = document.querySelector('.collection') || document.body;
  var observer = new MutationObserver(function () { injectCardButtons(); });
  observer.observe(observerTarget, { childList: true, subtree: true });

  window.PulzenaCompare = PulzenaCompare;
})();
